import React, { useCallback, useMemo, useState } from 'react';

import { Flex, Box, Text } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import BlockRemoveButton from '../../../BlockRemoveButton';
import InlineLoader from '../../../InlineLoader';
import UnstyledAnchor from '../../../UnstyledAnchor';
import { useFormsContext } from '../FormsProvider';
import { FORM_TEMPLATE_TYPE_TRANSLATION_MAP } from '../helpers/constants';
import { getTemplateUrl } from '../helpers/utils';
import { useCanUserCreateForm } from '../hooks';
import {
  useUserPermissionsByPgIdMap,
  useUsersByOxygenIdMap,
} from '../hooks/useEntitiesMap';

const FormTemplatesEntityBlock = ({
  entity: template,
  projectId,
  onRemoveClick,
  onReferenceClickAnalytics,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { data: users, loading: isLoadingUsers } = useUsersByOxygenIdMap();
  const {
    forgeUserMapping,
    isLoadingForgeUserMapping,
    pgProjectUsers,
    isLoadingPgProjectUsers,
  } = useFormsContext();
  const {
    data: userPermissionsMapByPGID,
    loading: isLoadingUserPermissionsMapByPGID,
  } = useUserPermissionsByPgIdMap();
  const canUserCreateForm = useCanUserCreateForm();
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);

  const handleRemoveClick = () => {
    onRemoveClick();
  };

  const memberVisibilityList = useMemo(() => {
    if (
      isLoadingUsers ||
      !template ||
      isLoadingPgProjectUsers ||
      isLoadingForgeUserMapping ||
      isLoadingUserPermissionsMapByPGID
    )
      return null;

    const visibleUids = new Set();
    const visibleNames = [];

    pgProjectUsers.forEach((pgProjectUser) => {
      const isVisible = canUserCreateForm(
        userPermissionsMapByPGID[pgProjectUser.user_id],
        template,
      );

      if (
        isVisible &&
        !visibleUids.has(pgProjectUser.user_id) &&
        users[forgeUserMapping[pgProjectUser.user_id]]
      ) {
        visibleUids.add(pgProjectUser.user_id);
        visibleNames.push(users[forgeUserMapping[pgProjectUser.user_id]].name);
      }
    });

    return visibleNames;
  }, [
    forgeUserMapping,
    users,
    pgProjectUsers,
    template,
    isLoadingUsers,
    isLoadingForgeUserMapping,
    isLoadingPgProjectUsers,
    isLoadingUserPermissionsMapByPGID,
    userPermissionsMapByPGID,
    canUserCreateForm,
  ]);

  const renderProjectMembers = useCallback(() => {
    return (
      <Flex alignItems="center">
        <Text typography="bodySmall" marginRight="5px">
          {T.translate('FORMS_CAN_BE_CREATED_BY')}
        </Text>

        {memberVisibilityList ? (
          <LinkButton
            style={theme.typography.bodySmall}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMemberModalOpen(true);
            }}
          >
            {`${memberVisibilityList.length} ${T.translate('PROJECT_MEMBERS')}`}
          </LinkButton>
        ) : (
          <Box width="100px">
            <InlineLoader />
          </Box>
        )}
      </Flex>
    );
  }, [T, memberVisibilityList]);

  const renderCreatedBy = useCallback(() => {
    if (
      !isLoadingForgeUserMapping &&
      users[forgeUserMapping[template.created_by]]
    ) {
      return `${T.translate('CREATED_BY')} ${
        users[forgeUserMapping[template.created_by]].name
      }`;
    }

    if (isLoadingUsers || isLoadingForgeUserMapping) {
      return <InlineLoader />;
    }

    // tech@plangrid.com does not have an Oxygen user
    return `${T.translate('CREATED_BY')} Autodesk`;
  }, [
    template,
    forgeUserMapping,
    users,
    isLoadingUsers,
    isLoadingForgeUserMapping,
    T,
  ]);

  return (
    <>
      <UnstyledAnchor
        style={[
          {
            display: 'flex',
            alignItems: 'center',
            padding: '18px 0px 20px 14px',
            height: '100px',
            cursor: 'pointer',
            overflow: 'hidden',
            borderBottom: `1px solid ${theme.colors.charcoal100}`,
            ['&:hover .FormsTemplatesEntityBlock__remove-button']: {
              visibility: 'visible',
            },
          },
        ]}
        onClick={onReferenceClickAnalytics}
        href={getTemplateUrl(projectId, template.uid, template.folder_uid)}
      >
        <Box overflow="hidden" flexGrow="1">
          <OverflowTooltip content={template.name}>
            <span style={theme.typography.bodyMediumBold}>{template.name}</span>
          </OverflowTooltip>
          <Flex alignItems="center" typography="bodySmall" marginTop="7px">
            <Box flexShrink="0">{renderCreatedBy()}</Box>
            {template.template_type && (
              <>
                <Divider
                  variant={Divider.VARIANTS.VERTICAL}
                  color={Divider.COLORS.DARK}
                  style={{ margin: '0px 12px 0px 12px' }}
                  height={12}
                />
                <Box typography="ellipsis" flexGrow="1">
                  {T.translate(
                    FORM_TEMPLATE_TYPE_TRANSLATION_MAP[template.template_type],
                  )}
                </Box>
              </>
            )}
          </Flex>
          <Box marginTop="6px">{renderProjectMembers()}</Box>
        </Box>
        {onRemoveClick && (
          <Flex
            alignItems="center"
            justifyContent="center"
            height="100%"
            paddingRight="15px"
          >
            <BlockRemoveButton
              className="FormsTemplatesEntityBlock__remove-button"
              style={{
                visibility: 'hidden',
              }}
              onClick={handleRemoveClick}
            />
          </Flex>
        )}
      </UnstyledAnchor>
      <ModalConfirmation
        backdrop
        noCancelButton
        confirmationButtonLabel={T.translate('OK')}
        height="auto"
        isConfirmationButtonAlert={false}
        message={memberVisibilityList?.join(', ')}
        show={isMemberModalOpen}
        size="small"
        title={T.translate('FORMS_CAN_BE_CREATED_BY')}
        onClose={() => {
          setIsMemberModalOpen(false);
        }}
        onConfirm={() => {
          setIsMemberModalOpen(false);
        }}
      />
    </>
  );
};

export default FormTemplatesEntityBlock;
